import React from "react"
import { useTranslation } from "react-i18next"

import { CurrencyField } from "@/components/fields/currency-field"
import { FlowDirectionTooltipContent } from "@/components/fields/flow-direction-tooltip-content"
import { FormDateRangeField } from "@/components/fields/form-date-range-field/form-date-range-field"
import { UsageUnitField } from "@/components/fields/usage-unit-field"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { FormTextField } from "@/nzds/forms/form-text-field/form-text-field"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { displayResourceCode } from "@/services/bill/bill-resource"
import type { Bill } from "@/services/core-api/types"
import { useFormatFlowDirection } from "@/utils/bill/format-flow-direction"
import { getDisplayValue } from "@/utils/value"

import Stack from "@mui/material/Stack"

import type { EditBillSchema } from "./edit-bill.schema"

interface BillEditableFieldsProps {
  bill: Bill
  canEditBillFields: boolean
  displayedLockedValue: boolean
  hasBillFieldChanges: boolean
  isBusy: boolean
}

export const BillEditableFields: React.FC<BillEditableFieldsProps> = ({
  bill,
  canEditBillFields,
  displayedLockedValue,
  hasBillFieldChanges,
  isBusy,
}) => {
  const { t } = useTranslation("common")
  const { formatFlowDirection } = useFormatFlowDirection()
  const areDateFieldsEditable = canEditBillFields && !displayedLockedValue
  const areFieldEditsDisabled =
    !canEditBillFields || isBusy || displayedLockedValue

  return (
    <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
      {areDateFieldsEditable ? (
        <FormDateRangeField disabled={areFieldEditsDisabled} />
      ) : (
        <>
          <ReadonlyField
            label={t("bill.periodStart")}
            value={getDisplayValue(bill.start_date)}
          />
          <ReadonlyField
            label={t("bill.periodEnd")}
            value={getDisplayValue(bill.end_date)}
          />
        </>
      )}
      <ReadonlyField
        label={t("fields.flowDirection.title")}
        tooltipContent={<FlowDirectionTooltipContent />}
        value={formatFlowDirection(bill.flow_direction)}
      />
      <ReadonlyField
        label={t("fields.sourceType.title")}
        value={getDisplayValue(bill.source_type)}
      />
      <ReadonlyField
        label={t("fields.resource.title")}
        value={displayResourceCode(bill.resource_type, (key) => t(key))}
      />
      <FormTextField<EditBillSchema>
        disabled={areFieldEditsDisabled}
        label={t("fields.usage.title")}
        name="usage"
        required
        variant="stacked"
      />
      <UsageUnitField
        currentValue={bill.usage_unit}
        disabled={areFieldEditsDisabled}
        required
      />
      <FormTextField<EditBillSchema>
        disabled={areFieldEditsDisabled}
        label={t("fields.cost.title")}
        name="cost"
        variant="stacked"
      />
      <CurrencyField
        currentValue={bill.currency}
        disabled={areFieldEditsDisabled}
      />
      <FormTextField<EditBillSchema>
        disabled={areFieldEditsDisabled}
        label={t("editBill.changeReasonLabel")}
        name="changeReason"
        required={hasBillFieldChanges}
        variant="stacked"
      />
    </Stack>
  )
}
